import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Zap, Users, Target, Award, ArrowRight, X, Upload, Send, Briefcase, MapPin, ChevronRight, Code } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import apiClient from "@/lib/api";
import { getOfferContractTypes } from "@/lib/jobOffers";
import { useApplicationForm } from "@/hooks/useApplicationForm";

// Careers Section
const CareersSection = () => {
  const navigate = useNavigate();
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobOffers, setJobOffers] = useState([]);
  const [totalJobCount, setTotalJobCount] = useState(0);
  const [hasMoreJobs, setHasMoreJobs] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(true);

  const {
    applicationData,
    cvFile,
    isSubmitting,
    handleApplicationChange,
    handleFileChange,
    clearCvFile,
    resetApplicationForm,
    submitApplication,
  } = useApplicationForm();

  // Fetch job offers from API - limit to 3 on homepage
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoadingJobs(true);
        const response = await apiClient.get("/jobs?limit=3&show_all=false");
        setJobOffers(response.data.offers);
        setTotalJobCount(response.data.total_count);
        setHasMoreJobs(response.data.has_more);
      } catch {
        setJobOffers([]);
        setTotalJobCount(0);
        setHasMoreJobs(false);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchJobs();
  }, []);

  const perks = [
    { icon: Zap, text: "Projets critiques Grands Comptes" },
    { icon: Users, text: "Encadrement technique par nos Leads" },
    { icon: Target, text: "Suivi de carrière de proximité" },
    { icon: Award, text: "Stabilité du groupe Proxiad" }
  ];

  const openApplicationForm = (job = null) => {
    setSelectedJob(job);
    setShowApplicationForm(true);
  };

  const closeApplicationForm = () => {
    setSelectedJob(null);
    setShowApplicationForm(false);
    resetApplicationForm("cv-upload");
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    const sent = await submitApplication({ selectedJob });
    if (sent) {
      closeApplicationForm();
    }
  };
  
  const selectedJobContractTypes = selectedJob ? getOfferContractTypes(selectedJob) : [];

  return (
    <section className="section-spacing relative noise-overlay">
      {/* Background - LIGHTER */}
      <div className="absolute inset-0 bg-[#070B1A]" />
      
      {/* Subtle background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1605379399642-870262d3d051?w=1200&q=50"
          alt=""
          className="w-full h-full object-cover opacity-[0.04]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070B1A] via-[#070B1A]/95 to-[#070B1A]" />
      </div>
      
      <div id="carrieres" className="lenvia-container relative z-10">
        <div className="grid md:grid-cols-2 gap-14 lg:gap-24 items-start">
          {/* Left - Info */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2.5 mb-6">
              <Code className="w-4 h-4 text-lenvia-violet" />
              <span className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase">
                Carrières Lenvia
              </span>
            </span>
            
            <h2 
              className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight text-white mb-7 leading-[1.08]" 
              data-testid="careers-title"
            >
              Rejoignez nos{" "}
              <span className="gradient-text">pôles d'expertise</span>
            </h2>
            
            <p 
              className="text-base sm:text-lg lg:text-xl text-slate-300 mb-12 leading-relaxed" 
              data-testid="careers-description"
            >
              Intervenez sur des projets critiques, dans un environnement structuré et exigeant. Concevez des systèmes complexes à fort impact métier, avec un véritable accompagnement technique.
            </p>

            {/* Perks */}
            <div className="flex flex-col gap-5 mb-12">
              {perks.map((perk, index) => (
                <div 
                  key={perk.text} 
                  className="flex items-center gap-4 group" 
                  data-testid={`perk-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center group-hover:bg-lenvia-violet/15 group-hover:border-lenvia-violet/25 transition-all flex-shrink-0">
                    <perk.icon className="w-5 h-5 text-lenvia-violet" />
                  </div>
                  <span className="text-slate-200 text-base font-semibold">{perk.text}</span>
                </div>
              ))}
            </div>

            <Button
              className="btn-primary text-base group px-10 w-full md:w-auto"
              onClick={() => openApplicationForm(null)}
              data-testid="careers-cta"
            >
              Candidature spontanée
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>

          {/* Right - Job Openings or Application Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <AnimatePresence mode="wait">
              {showApplicationForm ? (
                <motion.div
                  key="application-form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-white">
                        {selectedJob ? `Postuler : ${selectedJob.title}` : 'Candidature spontanée'}
                      </h3>
                      {selectedJob && (
                        <div className="mt-2 space-y-2">
                          {selectedJobContractTypes.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                              {selectedJobContractTypes.map((contractType) => (
                                <span
                                  key={`${selectedJob.id}-${contractType}`}
                                  className="rounded-full border border-lenvia-blue/30 bg-lenvia-blue/10 px-2.5 py-1 text-xs text-lenvia-blue"
                                >
                                  {contractType}
                                </span>
                              ))}
                            </div>
                          )}
                          {selectedJob.location && (
                            <p className="text-sm text-slate-400">{selectedJob.location}</p>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={closeApplicationForm}
                      className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
                      data-testid="close-application-form"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <form onSubmit={handleApplicationSubmit} className="glass-card p-6 md:p-7 space-y-5" data-testid="application-form">
                    <div>
                      <label htmlFor="applicant-name" className="text-sm font-medium text-slate-300 mb-2 block">Nom complet *</label>
                      <Input
                        id="applicant-name"
                        type="text"
                        name="name"
                        value={applicationData.name}
                        onChange={handleApplicationChange}
                        placeholder="Jean Dupont"
                        required
                        className="form-input focus:ring-2 focus:ring-lenvia-blue/50 transition-all duration-300"
                        data-testid="input-applicant-name"
                      />
                    </div>

                    <div>
                      <label htmlFor="applicant-email" className="text-sm font-medium text-slate-300 mb-2 block">Email *</label>
                      <Input
                        id="applicant-email"
                        type="email"
                        name="email"
                        value={applicationData.email}
                        onChange={handleApplicationChange}
                        placeholder="jean.dupont@email.com"
                        required
                        className="form-input focus:ring-2 focus:ring-lenvia-blue/50 transition-all duration-300"
                        data-testid="input-applicant-email"
                      />
                    </div>

                    <div>
                      <label htmlFor="applicant-phone" className="text-sm font-medium text-slate-300 mb-2 block">Téléphone</label>
                      <Input
                        id="applicant-phone"
                        type="tel"
                        name="phone"
                        value={applicationData.phone}
                        onChange={handleApplicationChange}
                        placeholder="+33 6 12 34 56 78"
                        className="form-input focus:ring-2 focus:ring-lenvia-blue/50 transition-all duration-300"
                        data-testid="input-applicant-phone"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-slate-400 mb-2 block">CV (PDF uniquement, max 5 Mo) *</label>
                      <div className="relative">
                        <input
                          type="file"
                          id="cv-upload"
                          accept=".pdf"
                          onChange={handleFileChange}
                          className="hidden"
                          data-testid="input-cv-upload"
                        />
                        <label
                          htmlFor="cv-upload"
                          className="flex items-center justify-center gap-3 w-full p-4 border border-dashed border-white/10 rounded-xl cursor-pointer hover:border-lenvia-blue/40 hover:bg-white/[0.02] transition-all"
                        >
                          <Upload className="w-5 h-5 text-lenvia-blue" />
                          <span className="text-slate-300">
                            {cvFile ? cvFile.name : "Télécharger votre CV"}
                          </span>
                        </label>
                        {cvFile && (
                          <button
                            type="button"
                            onClick={() => clearCvFile("cv-upload")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-400 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="btn-primary w-full justify-center"
                      disabled={isSubmitting}
                      data-testid="submit-application"
                    >
                      {isSubmitting ? "Envoi en cours..." : "Envoyer ma candidature"}
                      <Send className="w-4 h-4" />
                    </Button>
                    <p className="text-center text-[11px] text-slate-500 mt-3 px-2 leading-tight">
                      En soumettant ce formulaire, vous acceptez que vos données soient traitées conformément à notre <a href="/confidentialite" className="underline hover:text-white transition-colors">Politique de confidentialité</a>.
                    </p>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  key="job-openings"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-heading text-xl font-semibold text-white">
                      Postes ouverts {totalJobCount > 0 && <span className="text-lenvia-blue">({totalJobCount})</span>}
                    </h3>
                  </div>

                  {loadingJobs ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="glass-card p-6 animate-pulse">
                          <div className="h-5 bg-white/5 rounded w-3/4 mb-3"></div>
                          <div className="h-4 bg-white/[0.03] rounded w-1/2 mb-3"></div>
                          <div className="h-3 bg-white/[0.03] rounded w-1/3"></div>
                        </div>
                      ))}
                    </div>
                  ) : jobOffers.length > 0 ? (
                    <>
                      <div className="space-y-4">
                        {jobOffers.map((job, index) => {
                          const contractTypes = getOfferContractTypes(job);

                          return (
                            <motion.div
                              key={job.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                              whileHover={{ y: -2 }}
                              className="glass-card p-6 cursor-pointer group"
                              onClick={() => navigate(`/offre/${job.id}`)}
                              data-testid={`job-${index}`}
                            >
                              <div className="flex items-start justify-between mb-3">
                                <h4 className="font-heading font-semibold text-white group-hover:text-lenvia-blue transition-colors">
                                  {job.title}
                                </h4>
                                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-lenvia-blue group-hover:translate-x-1 transition-all flex-shrink-0" />
                              </div>
                              <div className="flex flex-wrap items-center gap-4 mb-3 text-sm text-slate-400">
                                {contractTypes.length > 0 && (
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Briefcase className="w-4 h-4" />
                                    {contractTypes.map((contractType) => (
                                      <span
                                        key={`${job.id}-${contractType}`}
                                        className="rounded-full border border-white/[0.06] bg-white/[0.03] px-2 py-1 text-xs text-slate-300"
                                      >
                                        {contractType}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {job.location && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    {job.location}
                                  </span>
                                )}
                                {job.department && (
                                  <span className="text-xs px-2.5 py-1 bg-lenvia-violet/15 rounded-full text-lenvia-violet border border-lenvia-violet/20">
                                    {job.department}
                                  </span>
                                )}
                              </div>
                              {job.salary_range && (
                                <p className="text-sm text-slate-500">{job.salary_range}</p>
                              )}
                            </motion.div>
                          );
                        })}
                      </div>

                      {hasMoreJobs && (
                        <Button
                          variant="outline"
                          className="btn-secondary w-full justify-center mt-6"
                          onClick={() => navigate("/carrieres")}
                          data-testid="show-all-jobs"
                        >
                          Voir toutes les offres ({totalJobCount})
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  ) : (
                    <div className="glass-card p-8 text-center">
                      <Briefcase className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                      <p className="text-slate-400 mb-4">Aucune offre disponible pour le moment.</p>
                      <Button
                        className="btn-primary"
                        onClick={() => openApplicationForm(null)}
                      >
                        Candidature spontanée
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CareersSection;
